// ---------------------------------------------------------------------------
// Digital Notice Board — Boards list (module landing)
// ---------------------------------------------------------------------------
// Gallery of saved boards with a live mini-preview of each. From here a school
// user creates a new board, opens one in the studio, projects it straight to the
// display, duplicates or deletes it. Opening the studio simply records which
// board is active (localStorage) and routes to the designer.
// ---------------------------------------------------------------------------
import React, {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    SimpleGrid,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {FaClone, FaPencilAlt, FaPlay, FaPlus, FaTrash} from "react-icons/fa";

import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import ElementVisual from "../../components/ElementVisual";
import ProjectionView from "../../components/ProjectionView";
import MarqueeStyle from "../../components/MarqueeStyle";
import {
    deleteBoard,
    duplicateBoard,
    listBoards,
    saveBoard,
} from "../../services/NoticeBoardService";
import {makeBoard} from "../../utils/noticeBoardUtils";

const ACTIVE_KEY = "nb.activeBoardId";
const PREVIEW_W = 300;

// Non-interactive first-slide preview that scales the real elements down.
function BoardPreview({board}) {
    const ref = useRef(null);
    const [scale, setScale] = useState(PREVIEW_W / board.canvas.width);
    useEffect(() => {
        if (ref.current) setScale(ref.current.clientWidth / board.canvas.width);
    }, [board.canvas.width]);
    const slide = board.slides[0];
    const height = board.canvas.height * scale;
    return (
        <Box ref={ref} position="relative" w="100%" h={`${height}px`} bg={slide.background} overflow="hidden" borderRadius="10px">
            {slide.elements.map((el) => (
                <Box
                    key={el.id}
                    position="absolute"
                    left={`${el.x * scale}px`}
                    top={`${el.y * scale}px`}
                    w={`${el.w * scale}px`}
                    h={`${el.h * scale}px`}
                    transform={el.rotation ? `rotate(${el.rotation}deg)` : undefined}
                    pointerEvents="none"
                >
                    <ElementVisual element={el} scale={scale} />
                </Box>
            ))}
        </Box>
    );
}

export default function NoticeBoards() {
    const history = useHistory();
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const border = useColorModeValue("gray.100", "gray.600");

    const [boards, setBoards] = useState([]);
    const [projectBoard, setProjectBoard] = useState(null);

    const refresh = () => setBoards(listBoards().data);
    useEffect(() => {
        refresh();
    }, []);

    const openDesigner = (id) => {
        localStorage.setItem(ACTIVE_KEY, id);
        history.push("/admin/notice-board/designer");
    };

    const createBoard = () => {
        const b = makeBoard("Untitled Board");
        saveBoard(b);
        openDesigner(b.id);
    };

    const onDuplicate = (id) => {
        duplicateBoard(id);
        refresh();
        toast({title: "Board duplicated", status: "success", duration: 2000});
    };

    const onDelete = (id) => {
        if (!window.confirm("Delete this notice board? This cannot be undone.")) return;
        deleteBoard(id);
        refresh();
    };

    return (
        <Box>
            <MarqueeStyle />
            <Flex justify="space-between" align="center" mb="24px" wrap="wrap" gap="10px">
                <Box>
                    <Text fontSize="xl" fontWeight="bold" color={textColor}>
                        Digital Notice Boards
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        Design screens once, project them to any TV or LED display.
                    </Text>
                </Box>
                <Button colorScheme="blue" leftIcon={<FaPlus />} onClick={createBoard}>
                    New Board
                </Button>
            </Flex>

            {boards.length === 0 ? (
                <Card>
                    <CardBody>
                        <Flex direction="column" align="center" py="60px" gap="12px">
                            <Text color="gray.500">No notice boards yet.</Text>
                            <Button colorScheme="blue" leftIcon={<FaPlus />} onClick={createBoard}>
                                Create your first board
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>
            ) : (
                <SimpleGrid columns={{base: 1, md: 2, xl: 3}} spacing="20px">
                    {boards.map((board) => (
                        <Card key={board.id} p="0" overflow="hidden">
                            <Box
                                cursor="pointer"
                                onClick={() => openDesigner(board.id)}
                                borderBottom="1px solid"
                                borderColor={border}
                            >
                                <BoardPreview board={board} />
                            </Box>
                            <Box p="16px">
                                <Flex justify="space-between" align="center" mb="4px">
                                    <Text fontWeight="bold" color={textColor} isTruncated>
                                        {board.name}
                                    </Text>
                                    <Badge colorScheme="blue" borderRadius="6px">
                                        {board.canvas.preset}
                                    </Badge>
                                </Flex>
                                <Text fontSize="xs" color="gray.500" mb="12px">
                                    {board.slides.length} slide{board.slides.length !== 1 ? "s" : ""}
                                </Text>
                                <Flex justify="space-between" align="center">
                                    <Button
                                        size="sm"
                                        colorScheme="green"
                                        leftIcon={<FaPlay />}
                                        onClick={() => setProjectBoard(board)}
                                    >
                                        Project
                                    </Button>
                                    <HStack spacing="4px">
                                        <IconButton size="sm" variant="ghost" aria-label="Edit" icon={<FaPencilAlt />} onClick={() => openDesigner(board.id)} />
                                        <IconButton size="sm" variant="ghost" aria-label="Duplicate" icon={<FaClone />} onClick={() => onDuplicate(board.id)} />
                                        <IconButton size="sm" variant="ghost" colorScheme="red" aria-label="Delete" icon={<FaTrash />} onClick={() => onDelete(board.id)} />
                                    </HStack>
                                </Flex>
                            </Box>
                        </Card>
                    ))}
                </SimpleGrid>
            )}

            {projectBoard && (
                <ProjectionView board={projectBoard} startIndex={0} onClose={() => setProjectBoard(null)} />
            )}
        </Box>
    );
}
